<template>
    <div class="max-w-6xl mx-auto pb-6 space-y-6">
        <h1 class="text-2xl font-bold text-gray-800">Pacientes</h1>

        <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
                <p class="text-sm text-gray-500">{{ totalPatients }} paciente{{ totalPatients !== 1 ? 's' : '' }}</p>
                <p class="text-xs text-gray-400">Busque por nome, email, telefone ou documento (CPF/CNPJ).</p>
            </div>
            <BaseButton :to="{ name: 'patientForm' }" skin="primary-small">+ Adicionar</BaseButton>
        </div>

        <div class="relative">
            <span class="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400 pointer-events-none">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
                </svg>
            </span>
            <input
                v-model="searchInput"
                type="text"
                placeholder="Buscar por nome, email, telefone ou CPF/CNPJ..."
                class="input input-bordered w-full pl-10 pr-10 text-sm"
                @keyup.enter="applySearch"
            />
            <button
                v-if="searchInput"
                type="button"
                class="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                title="Limpar"
                @click="clearSearch"
            >&#10005;</button>
        </div>

        <div v-if="activeSearch" class="flex flex-wrap items-center gap-2 text-xs text-gray-500 -mt-3">
            <span>Filtrado por: <strong class="text-gray-700">"{{ activeSearch }}"</strong></span>
            <button type="button" class="text-cyan-600 hover:text-cyan-700 font-semibold" @click="clearSearch">Limpar filtro &#10005;</button>
        </div>

        <div v-if="loading" class="space-y-3">
            <div v-for="i in 6" :key="i" class="bg-white border border-gray-100 rounded-2xl shadow-sm px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-4">
                <div class="flex items-center gap-3 sm:w-64 shrink-0">
                    <div class="skeleton w-11 h-11 rounded-full shrink-0"></div>
                    <div class="flex-1 space-y-2">
                        <div class="skeleton h-4 w-32 rounded"></div>
                        <div class="skeleton h-3 w-44 rounded"></div>
                    </div>
                </div>
                <div class="flex gap-1.5 sm:flex-1">
                    <div class="skeleton h-5 w-16 rounded-full"></div>
                    <div class="skeleton h-5 w-16 rounded-full"></div>
                </div>
                <div class="flex gap-6 sm:shrink-0">
                    <div class="skeleton h-6 w-28 rounded"></div>
                    <div class="skeleton h-6 w-28 rounded"></div>
                </div>
            </div>
        </div>

        <Alert
            :text="activeSearch ? 'Nenhum paciente encontrado para esta busca' : 'Ainda não existem pacientes cadastrados'"
            :show="!patients.length && !loading"
        />

        <div v-if="!loading && patients.length" class="space-y-3">
            <div
                v-for="patient in patients"
                :key="patient.id"
                class="w-full bg-white border border-gray-100 rounded-2xl shadow-sm px-5 py-4 hover:shadow-md hover:border-gray-200 transition-all cursor-pointer flex flex-col sm:flex-row sm:items-center gap-4"
                @click="goToPatient(patient.uuid)"
            >
                <div class="flex items-center gap-3 sm:w-64 shrink-0 min-w-0">
                    <img
                        v-if="patient.photo_url"
                        :src="patient.photo_url"
                        class="w-11 h-11 rounded-full object-cover shrink-0"
                        alt=""
                    />
                    <div v-else class="w-11 h-11 rounded-full bg-slate-200 flex items-center justify-center text-slate-500 font-semibold shrink-0">
                        {{ initials(patient.name) }}
                    </div>
                    <div class="min-w-0">
                        <div class="font-semibold text-gray-800 truncate">{{ patient.name }}</div>
                        <div class="text-xs text-gray-400 truncate">{{ patient.email || '—' }}</div>
                    </div>
                </div>

                <div class="flex flex-wrap items-center gap-1.5 sm:flex-1 min-h-[1.375rem]">
                    <template v-if="modalityCount(patient)">
                        <span v-for="(m, i) in modalityVisible(patient)" :key="i" :class="adminBadgeClass('neutral')">{{ m }}</span>
                        <span v-if="modalityExtra(patient) > 0" :class="adminBadgeClass('neutral')">+{{ modalityExtra(patient) }}</span>
                    </template>
                    <span v-else class="text-gray-300 text-xs">Sem modalidade cadastrada</span>
                </div>

                <div class="flex items-center gap-6 text-xs pt-3 sm:pt-0 border-t sm:border-t-0 sm:border-l border-gray-100 sm:pl-5 sm:shrink-0">
                    <div>
                        <div class="text-gray-400">Cliente desde:</div>
                        <div class="mt-0.5 whitespace-nowrap">
                            <span class="text-gray-700 font-medium">{{ formatDate(patient.created_at) }}</span>
                            <span class="text-gray-400">· {{ formatRelative(patient.created_at) }}</span>
                        </div>
                    </div>
                    <div>
                        <div class="text-gray-400">Última avaliação:</div>
                        <div class="mt-0.5 whitespace-nowrap">
                            <template v-if="patient.last_evaluation">
                                <span class="text-gray-700 font-medium">{{ formatDate(patient.last_evaluation) }}</span>
                                <span class="text-gray-400">· {{ formatRelative(patient.last_evaluation) }}</span>
                            </template>
                            <span v-else class="text-gray-400 font-medium">Nunca</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <PaginationV2 v-show="patients.length && !loading" :total="totalPatients" :limit="limit" :current="page" item-label="paciente" @changePage="changePagination"></PaginationV2>
    </div>
</template>

<script>

import { ref, onMounted } from 'vue';
import Swal from 'sweetalert';
import Alert from '@/components/Alert';
import { PatientService } from '../services/Entity';
import PaginationV2 from '../components/PaginationV2.vue';
import { useRouter } from 'vue-router';
import { adminBadgeClass } from '@/constants/adminBadge';

export default {
    components: {
        PaginationV2,
        Alert
    },
    setup() {
        const loading = ref(false);
        const totalPatients = ref(0);
        const page = ref(1);
        const limit = ref(10);
        const patients = ref([]);
        const searchInput = ref('');
        const activeSearch = ref('');
        const router = useRouter();

        onMounted(async () => {
            await loadPatients();
        });

        async function loadPatients(search) {
            try {
                loading.value = true;
                const filter = search ? { search } : {};
                const { data: { items, total } } = await PatientService.list(page.value, limit.value, filter);

                patients.value = items;
                totalPatients.value = total;

                loading.value = false;
            } catch(err) {
                loading.value = false;
                console.log(err);

                Swal({
                    title: 'Ocorreu um problema ao listar pacientes',
                    text: 'Atualize a página e tente novamente, se o erro persistir entre em contato com o suporte',
                    icon: 'error'
                });
            }
        }

        function applySearch() {
            page.value = 1;
            const term = searchInput.value.trim();
            activeSearch.value = term;
            loadPatients(term);
        }

        function clearSearch() {
            searchInput.value = '';
            activeSearch.value = '';
            page.value = 1;
            loadPatients();
        }

        async function changePagination(currentPage) {
            page.value = currentPage;
            await loadPatients(activeSearch.value);
        }

        async function goToPatient(patientUUID) {
            if (!patientUUID) return;

            router.push({ name: 'records', params: { uuid: patientUUID } });
        }

        function initials(name) {
            if (!name) return '?';
            const parts = name.trim().split(' ').filter(Boolean);
            if (!parts.length) return '?';
            if (parts.length === 1) return parts[0][0].toUpperCase();
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }

        function formatDate(dateStr) {
            if (!dateStr) return '—';
            const date = new Date(dateStr);
            if (Number.isNaN(date.getTime())) return '—';
            return date.toLocaleDateString('pt-BR');
        }

        function formatRelative(dateStr) {
            if (!dateStr) return '';
            const date = new Date(dateStr);
            if (Number.isNaN(date.getTime())) return '';

            const diffSec = Math.floor((Date.now() - date.getTime()) / 1000);
            if (diffSec < 60) return 'agora';
            const diffMin = Math.floor(diffSec / 60);
            if (diffMin < 60) return `há ${diffMin} min`;
            const diffHour = Math.floor(diffMin / 60);
            if (diffHour < 24) return `há ${diffHour}h`;
            const diffDay = Math.floor(diffHour / 24);
            if (diffDay < 30) return `há ${diffDay} ${diffDay === 1 ? 'dia' : 'dias'}`;
            const diffMonth = Math.floor(diffDay / 30);
            if (diffMonth < 12) return `há ${diffMonth} ${diffMonth === 1 ? 'mês' : 'meses'}`;
            const diffYear = Math.floor(diffMonth / 12);
            return `há ${diffYear} ${diffYear === 1 ? 'ano' : 'anos'}`;
        }

        function modalityCount(patient) {
            return patient && patient.modality && patient.modality.length ? patient.modality.length : 0;
        }

        function modalityVisible(patient) {
            if (!patient || !patient.modality || !patient.modality.length) return [];
            return patient.modality.slice(0, 3).map(m => m.label || m.name || '—').filter(Boolean);
        }

        function modalityExtra(patient) {
            const count = modalityCount(patient);
            return count > 3 ? count - 3 : 0;
        }

        return {
            limit,
            page,
            goToPatient,
            loadPatients,
            applySearch,
            clearSearch,
            changePagination,
            totalPatients,
            loading,
            patients,
            searchInput,
            activeSearch,
            initials,
            formatDate,
            formatRelative,
            modalityCount,
            modalityVisible,
            modalityExtra,
            adminBadgeClass
        }
    },
}
</script>
